/**
 * Companio cart drawer.
 *
 * This is a CART script, not a modal script. Handover section 4.5 forbids a
 * second focus-trap implementation; the drawer avoids one entirely by using a
 * native <dialog> with showModal(), so the browser owns focus containment,
 * focus restore and background inertness. Nothing here duplicates
 * companio-modal.js.
 *
 * Three things in here are load-bearing and must not be "simplified":
 *
 * 1. reconcileGiftBox() IS DELIBERATELY ABSENT. The PDP hands its gift box over
 *    with ?companio_gift=1, which lands on /cart, and companio-cart.js is the
 *    only thing allowed to consume it. Running the same reconcile here would
 *    add a second gift box to the order.
 * 2. Quantity changes send the parent line AND its linked gift box line in ONE
 *    /cart/update.js call, so "one box per pair" can never drift.
 * 3. Cart links are intercepted from the document rather than by editing the
 *    35.6KB header. With JS off the links still navigate to /cart, which works.
 *
 * localStorage is wrapped in try/catch: in a private window getItem THROWS
 * rather than returning null, and an unguarded read would kill the whole script
 * and take the drawer with it. Storage unavailable degrades to "no recently
 * viewed", never to a broken cart.
 */

const RV_KEY = 'companio:recently-viewed';
const RV_MAX = 12;

function readViewed() {
  try {
    const raw = window.localStorage.getItem(RV_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((handle) => typeof handle === 'string') : [];
  } catch (error) {
    return [];
  }
}

function writeViewed(list) {
  try {
    window.localStorage.setItem(RV_KEY, JSON.stringify(list.slice(0, RV_MAX)));
  } catch (error) {
    /* private window, blocked site data: recently viewed simply stays empty */
  }
}

class CompanioCartDrawer extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    this.cartUrl = this.dataset.cartUrl || '/cart';
    this.limit = Math.max(1, Number(this.dataset.rvLimit || 2));
    this.dialog = this.querySelector('[data-cd-dialog]');
    this.busy = false;
    this.handle = this.currentHandle();

    this.onDocumentClick = this.onDocumentClick.bind(this);

    this.recordView();
    this.paintRecentlyViewed();

    this.addEventListener('click', (event) => this.onClick(event));
    this.addEventListener('input', (event) => this.onInput(event));
    document.addEventListener('click', this.onDocumentClick);

    if (this.dialog) {
      this.dialog.addEventListener('click', (event) => this.onDialogClick(event));

      // Escape is normally the browser's job on a modal <dialog>. Measured on
      // 2026-08-31: focus was inside the dialog, the keydown fired and was not
      // prevented, no `cancel` event arrived and the dialog stayed open. Rather
      // than leave a keyboard user with no way out, close it explicitly. This is
      // the ONLY piece of modal behaviour this file implements.
      this.dialog.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        event.preventDefault();
        this.close();
      });
    }
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClick);
  }

  /* ---------- recently viewed ---------- */

  /**
   * Liquid gives us the handle when the global `product` object is in scope.
   * The URL is the fallback, because a section rendered in the header group is
   * not guaranteed that object, and a silent "never records anything" would be
   * indistinguishable from "the visitor has not browsed yet".
   * The pattern also covers /collections/x/products/y.
   */
  currentHandle() {
    if (this.dataset.viewedHandle) return this.dataset.viewedHandle;
    const match = window.location.pathname.match(/\/products\/([^/?#]+)/);
    return match ? decodeURIComponent(match[1]) : '';
  }

  recordView() {
    if (!this.handle) return;
    const list = readViewed().filter((entry) => entry !== this.handle);
    list.unshift(this.handle);
    writeViewed(list);
  }

  /**
   * Liquid cannot read localStorage, so the section renders every candidate
   * product hidden and this picks the ones the visitor actually opened, in the
   * order they opened them. Nothing is shown if they have opened nothing — an
   * empty "recently viewed" heading over two arbitrary products would be a lie.
   */
  paintRecentlyViewed() {
    const wrap = this.querySelector('[data-cd-rv]');
    const list = this.querySelector('[data-cd-rv-list]');
    if (!wrap || !list) return;

    let shown = 0;

    list.querySelectorAll('[data-rv-handle]').forEach((item) => {
      item.hidden = true;
    });

    readViewed().forEach((handle) => {
      if (shown >= this.limit || handle === this.handle) return;
      let item = null;
      try {
        item = list.querySelector('[data-rv-handle="' + CSS.escape(handle) + '"]');
      } catch (error) {
        item = null;
      }
      if (!item) return;
      item.hidden = false;
      list.appendChild(item);
      shown += 1;
    });

    wrap.hidden = shown === 0;
  }

  /* ---------- open / close ---------- */

  open() {
    if (!this.dialog || this.dialog.open) return;
    this.dialog.showModal();
  }

  close() {
    if (this.dialog && this.dialog.open) this.dialog.close();
  }

  /**
   * Every cart entry point on the site is a plain <a href="/cart">, in the
   * header and anywhere else. Intercepting here means the header file is never
   * touched, and a visitor without JS still gets the working cart page.
   */
  onDocumentClick(event) {
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (!this.dialog || typeof this.dialog.showModal !== 'function') return;

    const link = event.target.closest && event.target.closest('a[href]');
    if (!link) return;
    if (link.hasAttribute('data-cd-allow')) return;
    if (link.target && link.target !== '_self') return;
    if (this.contains(link)) return;

    let url;
    try {
      url = new URL(link.href, window.location.origin);
    } catch (error) {
      return;
    }
    if (url.origin !== window.location.origin) return;

    const strip = (path) => path.replace(/\/+$/, '');
    if (strip(url.pathname) !== strip(this.cartUrl)) return;

    event.preventDefault();
    this.open();
  }

  onDialogClick(event) {
    const rect = this.dialog.getBoundingClientRect();
    const inside =
      event.clientX >= rect.left &&
      event.clientX <= rect.right &&
      event.clientY >= rect.top &&
      event.clientY <= rect.bottom;
    if (!inside) this.close();
  }

  /* ---------- delegated actions ---------- */

  onClick(event) {
    const hit = (selector) => event.target.closest(selector);
    let el;

    if (hit('[data-cd-close]')) return this.close();
    if ((el = hit('[data-cd-qty-plus]'))) return this.changeQuantity(el.dataset.cdQtyPlus, 1);
    if ((el = hit('[data-cd-qty-minus]'))) return this.changeQuantity(el.dataset.cdQtyMinus, -1);
    if ((el = hit('[data-cd-remove-gift]'))) return this.mutate({ [el.dataset.cdRemoveGift]: 0 });
    if ((el = hit('[data-cd-confirm-remove]'))) {
      return this.removeLine(el.dataset.cdConfirmRemove, el.dataset.giftKey);
    }
    if (hit('[data-cd-cancel-remove]')) return this.hideConfirms();
    if ((el = hit('[data-cd-remove]'))) return this.requestRemove(el);
    if (hit('[data-cd-note-toggle]')) return this.toggleNote();
    if (hit('[data-cd-note-confirm]')) return this.saveNote();
    if ((el = hit('[data-cd-add]'))) return this.quickAdd(el.dataset.cdAdd);
    return undefined;
  }

  onInput(event) {
    if (!event.target.matches('[data-cd-note-field]')) return;
    const error = this.querySelector('[data-cd-note-error]');
    if (error) error.hidden = true;
  }

  /* ---------- cart mutations ---------- */

  changeQuantity(key, delta) {
    if (this.busy) return undefined;
    const line = this.line(key);
    if (!line) return undefined;
    const current = Number(line.querySelector('[data-cd-qty-value]')?.textContent || 1);
    const next = Math.max(1, current + delta);
    if (next === current) return undefined;

    const updates = { [key]: next };
    // The linked gift box follows its parent in the SAME request — "one box per
    // pair" is therefore atomic and the totals can never disagree.
    if (line.dataset.giftKey) updates[line.dataset.giftKey] = next;
    return this.mutate(updates);
  }

  /**
   * A line carrying uploaded photos cannot be undone: /cart/add.js has no way
   * to re-attach files, so removal is final. The page uses a modal for this;
   * a dialog inside a modal dialog is not workable, so the row asks inline.
   */
  requestRemove(button) {
    if (this.busy) return undefined;
    if (button.dataset.lineMode === 'custom') {
      const line = button.closest('[data-line-key]');
      const panel = line && line.querySelector('[data-cd-confirm]');
      if (panel) {
        this.hideConfirms();
        panel.hidden = false;
        panel.querySelector('[data-cd-cancel-remove]')?.focus();
        return undefined;
      }
    }
    return this.removeLine(button.dataset.cdRemove, button.dataset.giftKey);
  }

  removeLine(key, giftKey) {
    const updates = { [key]: 0 };
    if (giftKey) updates[giftKey] = 0;
    return this.mutate(updates);
  }

  hideConfirms() {
    this.querySelectorAll('[data-cd-confirm]').forEach((panel) => {
      panel.hidden = true;
    });
  }

  quickAdd(variantId) {
    if (this.busy || !variantId) return undefined;
    return this.request('/cart/add.js', { id: Number(variantId), quantity: 1 });
  }

  /* ---------- gift note ---------- */

  toggleNote() {
    const body = this.querySelector('[data-cd-note-body]');
    if (!body) return;
    const toggle = this.querySelector('[data-cd-note-toggle]');
    const plus = this.querySelector('[data-cd-note-plus]');
    const confirm = this.querySelector('[data-cd-note-confirm]');
    const opening = body.hidden;

    body.hidden = !opening;
    if (plus) plus.hidden = opening;
    if (confirm) confirm.hidden = !opening;
    if (toggle) toggle.setAttribute('aria-expanded', String(opening));

    // Coarse pointers: no autofocus. The keyboard would cover half the panel.
    if (opening && !window.matchMedia('(pointer: coarse)').matches) {
      body.querySelector('input')?.focus();
    }
  }

  /**
   * The note is a real $1 line item. It is marked with a hidden `_gift_note`
   * property — identification is by property, never by product handle, the same
   * rule the gift box follows. To / From / Note are ordinary properties so they
   * reach the order for whoever writes the card. Quantity is always 1.
   */
  saveNote() {
    if (this.busy) return undefined;
    const body = this.querySelector('[data-cd-note-body]');
    if (!body) return undefined;
    const error = this.querySelector('[data-cd-note-error]');

    const properties = { _gift_note: '1' };
    let filled = false;
    body.querySelectorAll('[data-cd-note-field]').forEach((field) => {
      const value = String(field.value || '').trim();
      if (!value) return;
      properties[field.dataset.cdNoteField] = value;
      filled = true;
    });

    if (!filled) {
      if (error) error.hidden = false;
      body.querySelector('[data-cd-note-field]')?.focus();
      return undefined;
    }
    if (error) error.hidden = true;

    const existing = this.querySelector('[data-cd-note-key]');
    if (existing) {
      return this.request('/cart/change.js', {
        id: existing.dataset.cdNoteKey,
        quantity: 1,
        properties,
      });
    }

    const variant = this.querySelector('[data-cd-note-variant]');
    if (!variant) return undefined;
    return this.request('/cart/add.js', {
      id: Number(variant.dataset.cdNoteVariant),
      quantity: 1,
      properties,
    });
  }

  /* ---------- transport ---------- */

  mutate(updates) {
    return this.request('/cart/update.js', { updates });
  }

  request(url, payload) {
    if (this.busy) return Promise.resolve(false);
    this.setBusy(true);
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(Object.assign({ sections: this.sectionId }, payload)),
    })
      .then((response) => (response.ok ? response.json() : Promise.reject(response)))
      .then((data) => {
        this.applySections(data.sections);
        return true;
      })
      .catch(() => {
        // Never leave a stale drawer sitting over a cart that changed underneath
        // it. The page is the source of truth.
        window.location.assign(this.cartUrl);
        return false;
      })
      .finally(() => this.setBusy(false));
  }

  /**
   * The re-rendered section carries the item count in a hidden span, so the
   * header badge costs nothing extra. /cart/add.js does not return the cart,
   * which is why the count is read from the markup rather than the JSON.
   */
  applySections(sections) {
    const html = sections && sections[this.sectionId];
    if (!html) return;
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const next = parsed.querySelector('[data-cd-inner]');
    const current = this.querySelector('[data-cd-inner]');
    if (!next || !current) return;

    const hadFocus = this.contains(document.activeElement);
    current.innerHTML = next.innerHTML;
    this.paintRecentlyViewed();

    const count = this.querySelector('[data-cd-count]');
    if (count) this.updateBadges(count.textContent);

    if (hadFocus) this.querySelector('[data-cd-close]')?.focus();
  }

  updateBadges(count) {
    if (count === undefined || count === null) return;
    document.querySelectorAll('[data-cart-count]').forEach((badge) => {
      badge.textContent = String(count).trim();
    });
  }

  line(key) {
    try {
      return this.querySelector('[data-line-key="' + CSS.escape(key) + '"]');
    } catch (error) {
      return null;
    }
  }

  setBusy(state) {
    this.busy = state;
    if (state) {
      this.setAttribute('data-busy', '');
    } else {
      this.removeAttribute('data-busy');
    }
  }
}

if (!customElements.get('companio-cart-drawer')) {
  customElements.define('companio-cart-drawer', CompanioCartDrawer);
}
